import {
  ArrowRightOutlined,
  MinusOutlined,
  PlusOutlined,
  CheckOutlined,
} from '@ant-design/icons';
import { Heading, Tooltip } from '@mm/components';
import { uniqueId } from 'lodash';
import React, { ReactNode, useState } from 'react';
import { BookDemoButton } from '../../../../../../common/buttons/BookDemoButton/BookDemoButton';
import { useBookDemo } from '../../../../../../common/buttons/BookDemoButton/use-book-demo';
import { PrimaryButton } from '../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import classNames from 'classnames';
import css from './ActivationBlock.module.css';
import { IN_APP_DEMO_UTM } from '../../../../../../dashboard/pages/XRayOnboarding/WebsiteReportSummary/constants';
import { useActivationSteps } from './use-activation-steps';
import { useWhitelabelConfig } from '@mm/hooks/src/use-whitelabel-config';

export type ActionProps = {
  keyName: string;
  icon: ReactNode;
  buttonName?: string;
  tooltip?: string;
  title: string;
  buttonClassName?: string;
  onClick?: () => void;
  hide?: boolean;
  component?: ReactNode;
  disabled?: boolean;
  completed?: boolean;
};

export const ActivationAction = ({
  keyName,
  icon,
  title,
  buttonName,
  tooltip,
  buttonClassName,
  onClick,
  component,
  disabled,
  completed,
}: ActionProps) => {
  const completedNotDisabled = completed && !disabled;
  const { pixelName = 'X-Ray Pixel' } = useWhitelabelConfig();

  return (
    <div
      className={classNames(css.channelContainer, {
        [css.completedAction]: completedNotDisabled,
      })}
      onClick={e => {
        e.preventDefault();
      }}
    >
      {icon}
      <div className={css.content}>
        <span className={css.contentTitle}>{title}</span>
        {component}
      </div>

      <PrimaryButton
        className={classNames(
          css.actionButton,
          css.buttonTooltip,
          buttonClassName,
        )}
        onClick={e => {
          e?.stopPropagation();
          onClick?.();
        }}
        disabled={disabled}
        color={completedNotDisabled ? 'transparent-no-shadow' : 'blue'}
      >
        {completedNotDisabled ? (
          <Tooltip
            title={buttonName}
            className={css.buttonTooltip}
            placement="bottom"
          >
            <div className={css.checkIcon}>
              <CheckOutlined className={css.checkCompleted} />
            </div>
          </Tooltip>
        ) : (
          <Tooltip
            title={disabled ? `Install ${pixelName}` : ''}
            className={css.buttonTooltip}
            placement="bottom"
          >
            {buttonName ? (
              buttonName
            ) : (
              <ArrowRightOutlined className={css.rightArrow} />
            )}
          </Tooltip>
        )}
      </PrimaryButton>
    </div>
  );
};

export const ActivationBlock = ({ className }: { className?: string }) => {
  const { actions } = useActivationSteps();

  const { isEligibleForDemo } = useBookDemo();

  const activeActions = actions.filter(action => !action.hide);

  const completedActionsCount = activeActions
    .map(a => a.completed)
    .filter(Boolean).length;

  const [listCollapsed, setListCollapsed] = useState(
    completedActionsCount >= 3,
  );

  if (completedActionsCount === activeActions.length) {
    return <></>;
  }

  return (
    <div className={classNames(css.container, className)}>
      <Heading
        size={2}
        className={css.heading}
        onClick={() => setListCollapsed(!listCollapsed)}
      >
        <div>
          Complete Account Setup (<span>{completedActionsCount}</span>/
          {activeActions.length})
        </div>
        <div className={css.collapser}>
          {listCollapsed ? (
            <PlusOutlined className={css.collapserIcon} />
          ) : (
            <MinusOutlined className={css.collapserIcon} />
          )}
        </div>
      </Heading>
      {!listCollapsed &&
        activeActions.map(action => (
          <ActivationAction key={uniqueId()} {...action} />
        ))}

      {isEligibleForDemo && (
        <div className={css.footer}>
          <div>Talk to an expert to take advantage of all our features</div>
          <BookDemoButton className={css.bookDemo} utm={IN_APP_DEMO_UTM} />
        </div>
      )}
    </div>
  );
};
